import type { CSSProperties } from "react";
import {
  STATUS_META,
  TYPE_META,
  PRIORITY_META,
  type Item,
  type ItemStatus,
  type ItemType,
  type Priority,
} from "@/lib/types";

export function StatusPill({ status }: { status: ItemStatus }) {
  const m = STATUS_META[status];
  return (
    <span
      style={{
        display: "inline-block",
        padding: "3px 10px",
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 500,
        background: m.bg,
        color: m.text,
        whiteSpace: "nowrap",
      }}
    >
      {m.label}
    </span>
  );
}

export function TypeBadge({ type }: { type: ItemType }) {
  const m = TYPE_META[type];
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        fontSize: 12.5,
        color: "var(--ink3)",
        whiteSpace: "nowrap",
      }}
    >
      <span style={{ color: m.color, fontSize: 12 }}>{m.glyph}</span>
      {m.label}
    </span>
  );
}

// §7: the parent link on a board card. Outlined rather than tonal so a linked
// task reads as *attached to* something, and an unlinked one — equally valid —
// simply has no pill.
export function ParentPill({ parent }: { parent: Item }) {
  const m = TYPE_META[parent.type];
  return (
    <span
      title={`Part of ${m.label.toLowerCase()} \u201c${parent.title}\u201d`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        maxWidth: "100%",
        minWidth: 0,
        padding: "2px 8px",
        borderRadius: 999,
        border: "1px solid var(--line2)",
        fontSize: 11,
        color: "var(--muted)",
        whiteSpace: "nowrap",
      }}
    >
      <span style={{ color: m.color, flex: "none" }}>{m.glyph}</span>
      <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{parent.title}</span>
    </span>
  );
}

export function PriorityDot({ priority }: { priority: Priority }) {
  const m = PRIORITY_META[priority];
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, color: "var(--ink3)" }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: m.color }} />
      {m.label}
    </span>
  );
}

export function Dot({ color, size = 7 }: { color: string; size?: number }) {
  return (
    <span
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: color,
        flex: "none",
        display: "inline-block",
      }}
    />
  );
}

export function ProgressBar({ value, color = "var(--accent)", height = 4 }: { value: number; color?: string; height?: number }) {
  return (
    <div style={{ height, borderRadius: 999, background: "var(--tint2)", overflow: "hidden" }}>
      <div style={{ height: "100%", width: `${Math.min(100, Math.max(0, value))}%`, background: color }} />
    </div>
  );
}

// Group headings for the list surfaces (Dashboard sections, ListView groups)
// live *outside* the rows now: a small colored uppercase label, a mono count,
// and a hairline rule. The color is what makes "Overdue" register before you
// read it. `tone` maps the well-known due buckets; other groupings fall back to
// the warn flag.
export function groupTone(title: string, warn?: boolean): string {
  switch (title) {
    case "Overdue": return "var(--bad)";
    case "Today": return "var(--accent)";
    case "Tomorrow": return "var(--accent)";
    case "This week": return "var(--ink2)";
    case "Later": return "var(--muted)";
    case "Completed": return "var(--good)";
    default: return warn ? "var(--bad)" : "var(--ink2)";
  }
}

export function GroupHeading({ label, count, tone }: { label: string; count: number; tone: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 8 }}>
      <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.4px", textTransform: "uppercase", color: tone }}>{label}</span>
      <span className="mono" style={{ fontSize: 12, color: "var(--muted2)" }}>{count}</span>
      <span style={{ flex: 1, height: 1, background: "var(--tint2)" }} />
    </div>
  );
}

export function GBadge() {
  return (
    <span
      title="Synced to Google Tasks"
      className="mono"
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 16,
        height: 16,
        borderRadius: 4,
        fontSize: 10,
        color: "var(--muted)",
        border: "1px solid var(--line2)",
      }}
    >
      G
    </span>
  );
}

export const card: CSSProperties = {
  background: "var(--surface)",
  border: "1px solid var(--line)",
  borderRadius: 14,
  boxShadow: "var(--shadowCard)",
};

export const eyebrow: CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: "var(--fs-eyebrow)",
  fontWeight: "var(--fw-title)",
  letterSpacing: "var(--ls-eyebrow)",
  textTransform: "uppercase",
  color: "var(--muted)",
};

export const h1: CSSProperties = {
  margin: "8px 0 0",
  fontSize: "var(--fs-h1)",
  fontWeight: "var(--fw-title)",
  letterSpacing: "var(--ls-title)",
};

// §1 type scale. Titles carry the hierarchy at weight 700; secondary text
// stays 500. Values come from the CSS tokens in index.css — change them there,
// not here. Import these instead of re-typing sizes/weights at call sites.
export const panelHeading: CSSProperties = {
  fontSize: "var(--fs-panel)",
  fontWeight: "var(--fw-title)",
};

/**
 * The board card's title.
 *
 * Not bold, and smaller than a row title. A board column is a stack of these:
 * at 15px/700 every card shouted, the cards were taller than their content
 * needed, and a full column ran off the screen. The weight was doing no work —
 * a card has one piece of text, so nothing is competing with it.
 */
export const cardTitle: CSSProperties = {
  fontSize: "var(--fs-card-title)",
  fontWeight: 400,
  lineHeight: 1.36,
  letterSpacing: "var(--ls-tight)",
  // A pasted URL is one unbreakable word; without this it sets the column width.
  overflowWrap: "anywhere",
};

export const rowTitle: CSSProperties = {
  fontSize: "var(--fs-row-title)",
  fontWeight: "var(--fw-title)",
  letterSpacing: "var(--ls-tight)",
};
